/**
 * Setter AI Call Analysis — UI.
 *
 * - SetterAiAnalysisToggle: per-account switch "Przeanalizuj rozmowy z AI" + token estimate.
 *   When on, the backend auto-transcribes + coaches every qualifying call.
 * - CallAnalysisButton + modal: per-call transcript + AI coaching (score 1-10), fullscreen,
 *   with on-demand (re)run. Polls while a call is being analyzed.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { apiRequest } from "@/lib/queryClient";
import { Sparkles, Loader2, X, RefreshCw, Star } from "lucide-react";

/**
 * The coaching HTML comes from our LLM pipeline, which (historically) styled it for a
 * light "Apple-style" background — dark inline colors that vanish on the dark modal, plus
 * <style>/layout cruft that breaks the page. Strip ALL styling + structural tags so only
 * semantic markup survives; the scoped .ringostat-analysis-html CSS themes it for dark.
 */
function sanitizeAnalysisHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    FORBID_TAGS: [
      "style",
      "script",
      "iframe",
      "link",
      "meta",
      "head",
      "html",
      "body",
      "title",
      "font",
    ],
    FORBID_ATTR: [
      "style",
      "class",
      "color",
      "bgcolor",
      "background",
      "align",
      "width",
      "height",
      "face",
    ],
  });
}

const withForUser = (url: string, forUserId?: string | null) =>
  forUserId ? `${url}${url.includes("?") ? "&" : "?"}forUserId=${forUserId}` : url;

/** Call-analysis endpoints live under the integration that owns the call source. */
type CallSource = "ringostat" | "ghl";
const sourceBase = (source?: CallSource | string | null) =>
  source === "ringostat" ? "/api/integrations/ringostat" : "/api/integrations/crm";

interface Estimate {
  appTokens: number;
  appTokensPerMinute: number;
}

const DEFAULT_ANALYSIS_MODEL = "anthropic/claude-sonnet-4";
const ANALYSIS_MODELS: { id: string; label: string; pricey?: boolean }[] = [
  { id: "anthropic/claude-sonnet-4", label: "Claude Sonnet 4 (domyślny)" },
  {
    id: "anthropic/claude-opus-4.8",
    label: "Claude Opus 4.8 — dokładniejszy, ~5× droższy",
    pricey: true,
  },
];

export function SetterAiAnalysisToggle({
  forUserId,
  initialEnabled,
  initialModel,
  source,
}: {
  forUserId?: string | null;
  initialEnabled?: boolean;
  initialModel?: string | null;
  source?: CallSource | string | null;
}) {
  const [enabled, setEnabled] = useState(!!initialEnabled);
  const [model, setModel] = useState(initialModel || DEFAULT_ANALYSIS_MODEL);
  const [saving, setSaving] = useState(false);

  // Estimate reflects the selected model's cost (Opus charges ~5× the tokens).
  const { data: est } = useQuery<Estimate>({
    queryKey: ["call-analysis-estimate", forUserId ?? "", model, source ?? ""],
    queryFn: () =>
      apiRequest(
        "GET",
        withForUser(
          `${sourceBase(source)}/analysis-estimate?durationSec=360&model=${encodeURIComponent(model)}`,
          forUserId
        )
      ),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    setSaving(true);
    try {
      await apiRequest("PATCH", "/api/analytics-settings/ai-call-analysis", {
        enabled: next,
        forClientUserId: forUserId ?? undefined,
      });
    } catch {
      setEnabled(!next); // revert on failure
    } finally {
      setSaving(false);
    }
  };

  const changeModel = async (next: string) => {
    const prev = model;
    setModel(next);
    setSaving(true);
    try {
      await apiRequest("PATCH", "/api/analytics-settings/ai-call-analysis", {
        model: next,
        forClientUserId: forUserId ?? undefined,
      });
    } catch {
      setModel(prev); // revert on failure
    } finally {
      setSaving(false);
    }
  };

  const pricey = ANALYSIS_MODELS.find((m) => m.id === model)?.pricey;

  return (
    <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Sparkles className="w-4 h-4 text-[#ef3e29] shrink-0" />
          <div>
            <div className="text-sm font-medium text-foreground">Przeanalizuj rozmowy z AI</div>
            <div className="text-xs text-muted-foreground">
              Auto-transkrypcja + coaching każdej dłuższej rozmowy
              {est ? ` · ~${est.appTokens} tokenów / rozmowę (~${est.appTokensPerMinute}/min)` : ""}
            </div>
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={saving}
          role="switch"
          aria-checked={enabled}
          className={`relative w-11 h-6 rounded-full transition-colors shrink-0 disabled:opacity-60 ${enabled ? "bg-[#ef3e29]" : "bg-white/15"}`}
        >
          <span
            className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${enabled ? "left-[22px]" : "left-0.5"}`}
          />
        </button>
      </div>
      {enabled && (
        <div className="flex items-center justify-between gap-3 pl-7">
          <label htmlFor="ai-analysis-model" className="text-xs text-muted-foreground">
            Model analizy
          </label>
          <select
            id="ai-analysis-model"
            value={model}
            onChange={(e) => changeModel(e.target.value)}
            disabled={saving}
            className="text-xs bg-background border border-border rounded-md px-2 py-1.5 text-foreground max-w-[60%] disabled:opacity-60"
          >
            {ANALYSIS_MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {enabled && pricey && (
        <p className="text-[11px] text-amber-400/80 pl-7 leading-tight">
          Opus 4.8 jest dokładniejszy, ale kosztuje ~5× więcej tokenów na rozmowę niż Sonnet.
        </p>
      )}
    </div>
  );
}

interface AnalysisData {
  status: "none" | "pending" | "transcribing" | "analyzing" | "done" | "failed";
  transcript?: string | null;
  analysisHtml?: string | null;
  score?: number | null;
  summary?: string | null;
  error?: string | null;
}

export function CallAnalysisButton({
  callId,
  forUserId,
  status,
  score,
  source,
}: {
  callId: string;
  forUserId?: string | null;
  status?: string | null;
  score?: number | null;
  source?: CallSource | string | null;
}) {
  const [open, setOpen] = useState(false);

  const label =
    score != null
      ? `${score}/10`
      : status === "analyzing" || status === "transcribing" || status === "pending"
        ? "Analiza…"
        : status === "failed"
          ? "Błąd"
          : "Analiza AI";

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-white/10 hover:bg-white/[0.06] text-white/70 transition-colors"
      >
        {score != null ? (
          <Star className="w-3 h-3 text-amber-400" />
        ) : (
          <Sparkles className="w-3 h-3 text-[#ef3e29]" />
        )}
        {label}
      </button>
      {open && (
        <CallAnalysisModal
          callId={callId}
          forUserId={forUserId}
          source={source}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function CallAnalysisModal({
  callId,
  forUserId,
  source,
  onClose,
}: {
  callId: string;
  forUserId?: string | null;
  source?: CallSource | string | null;
  onClose: () => void;
}) {
  const [triggering, setTriggering] = useState(false);
  const base = sourceBase(source);

  const { data, refetch } = useQuery<AnalysisData>({
    queryKey: ["call-analysis", callId, forUserId ?? "", source ?? ""],
    queryFn: () => apiRequest("GET", withForUser(`${base}/call-analysis/${callId}`, forUserId)),
    retry: false,
    refetchInterval: (q) => {
      const s = (q.state.data as AnalysisData | undefined)?.status;
      return s === "analyzing" || s === "transcribing" || s === "pending" ? 4000 : false;
    },
  });

  const run = async () => {
    setTriggering(true);
    try {
      await apiRequest("POST", `${base}/analyze-call`, {
        callId,
        forUserId: forUserId ?? undefined,
      });
      await refetch();
    } finally {
      setTriggering(false);
    }
  };

  const status = data?.status;
  const inProgress = status === "analyzing" || status === "transcribing" || status === "pending";

  // Portal to <body> so an ancestor with transform/filter (e.g. the call table) can't
  // trap this fixed overlay inside its containing block.
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/80 overflow-y-auto" onClick={onClose}>
      <div
        className="max-w-4xl mx-auto my-8 bg-[#0a0c10] border border-white/10 rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0a0c10] rounded-t-2xl">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-[#ef3e29]" />
            <div>
              <div className="text-base font-semibold text-white">Analiza rozmowy AI</div>
              {data?.summary && (
                <div className="text-xs text-white/50 max-w-xl truncate">{data.summary}</div>
              )}
            </div>
            {data?.score != null && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400/15 text-amber-300 text-sm font-bold">
                <Star className="w-3.5 h-3.5" /> {data.score}/10
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={run}
              disabled={triggering || inProgress}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-white/10 hover:bg-white/[0.06] disabled:opacity-50 text-white/70"
            >
              {triggering || inProgress ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              {status === "done" ? "Przeanalizuj ponownie" : "Przeanalizuj"}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-white/[0.06] text-white/50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {(!status || status === "none") && (
            <div className="text-center py-12 text-white/50 text-sm">
              Ta rozmowa nie była jeszcze analizowana. Kliknij „Przeanalizuj" — z nagrania zrobimy
              transkrypt i coaching.
            </div>
          )}
          {inProgress && (
            <div className="flex items-center justify-center gap-3 py-12 text-white/60 text-sm">
              <Loader2 className="w-5 h-5 animate-spin text-[#ef3e29]" />
              {status === "transcribing" ? "Transkrypcja nagrania…" : "Analiza rozmowy przez AI…"}
            </div>
          )}
          {status === "failed" && (
            <div className="rounded-lg border border-red-500/20 bg-red-500/[0.06] p-4 text-sm text-red-300">
              Nie udało się przeanalizować: {data?.error || "nieznany błąd"}
            </div>
          )}
          {status === "done" && (
            <>
              {data?.analysisHtml && (
                <div
                  className="ringostat-analysis-html text-sm text-white/85 leading-relaxed"
                  // Sanitized: strips the LLM's light-theme inline styles/colors so our
                  // scoped dark CSS controls the appearance (see index.css).
                  dangerouslySetInnerHTML={{ __html: sanitizeAnalysisHtml(data.analysisHtml) }}
                />
              )}
              {data?.transcript && (
                <details className="rounded-lg border border-white/10 bg-white/[0.02]">
                  <summary className="cursor-pointer select-none px-4 py-2.5 text-xs uppercase tracking-wide text-white/40">
                    Transkrypcja
                  </summary>
                  <p className="px-4 pb-4 text-sm text-white/70 whitespace-pre-wrap leading-relaxed">
                    {data.transcript}
                  </p>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
